import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getSerialDCRRegister, updateItemSerialNumbers, exportSerialDCRRegister, getWarehouses, updateOrderDetails } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
const isAdmin = userData.role === 'superAdmin' || userData.role === 'admin' || userData.access?.includes('ops');
const canEdit = userData.role === 'superAdmin' || userData.role === 'admin';
const isOperationOnly = isAdmin && !canEdit;
const DCR_STATUS_OPTIONS = [
  { value: 'sent_for_approval',    label: 'Sent for Approval' },
  { value: 'approved_dcr_pending', label: 'Approved: DCR Pending' },
  { value: 'dcr_generated',        label: 'DCR Generated' },
];

const DCR_STATUS_META = {
  sent_for_approval:    { label: 'Sent for Approval',     cls: 'status-badge status-shipped' },
  approved_dcr_pending: { label: 'Approved: DCR Pending', cls: 'status-badge status-pending' },
  dcr_generated:        { label: 'DCR Generated',         cls: 'status-badge status-confirmed' },
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    minHeight: '32px',
    border: s.isFocused ? '1px solid #0d6efd' : '1px solid #9aa9b8',
    borderRadius: '4px',
    fontSize: '12px',
    boxShadow: s.isFocused ? '0 0 0 2px rgba(13,110,253,0.25)' : 'none',
    '&:hover': { borderColor: s.isFocused ? '#0d6efd' : '#ced4da' },
  }),
  placeholder: (p) => ({ ...p, color: '#6c757d', fontSize: '12px' }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? '#0d6efd' : s.isFocused ? '#f8f9fa' : 'white',
    color: s.isSelected ? 'white' : '#212529',
    fontSize: '12px',
    padding: '8px 12px',
    cursor: 'pointer',
  }),
  singleValue: (p) => ({ ...p, fontSize: '12px', color: '#212529' }),
  menu: (p) => ({ ...p, zIndex: 9999, fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e9ecef' }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
  valueContainer: (p) => ({ ...p, padding: '2px 8px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (p) => ({ ...p, padding: '4px' }),
  clearIndicator: (p) => ({ ...p, padding: '4px' }),
};

const customTableStyles = {
  headRow: {
    style: {
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
      borderTopColor: '#dee2e6',
      backgroundColor: '#f8f9fa',
      minHeight: '36px',
    },
  },
  headCells: {
    style: {
      '&:not(:last-of-type)': { borderRightStyle: 'solid', borderRightWidth: '1px', borderRightColor: '#dee2e6' },
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: '11px',
      color: '#495057',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      padding: '8px 10px',
    },
  },
  cells: {
    style: {
      '&:not(:last-of-type)': { borderRightStyle: 'solid', borderRightWidth: '1px', borderRightColor: '#dee2e6' },
      padding: '8px 10px',
      fontSize: '12px',
    },
  },
  rows: {
    style: {
      '&:hover': { backgroundColor: '#f8f9fa', cursor: 'default' },
      borderBottomColor: '#dee2e6',
      minHeight: '44px',
    },
  },
  pagination: {
    style: { borderTop: '1px solid #dee2e6', padding: '10px', minHeight: '40px', fontSize: '11px' },
  },
};

const SerialDCRRegister = () => {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(20);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' | null

  const FILTER_KEY = 'serialDcrFilters';

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY);
      return saved ? JSON.parse(saved) : { dateFrom: '', dateTo: '', warehouseId: null, dcrStatus: null, orderId: '' };
    } catch { return { dateFrom: '', dateTo: '', warehouseId: null, dcrStatus: null, orderId: '' }; }
  });

  const [debouncedOrderId, setDebouncedOrderId] = useState(filters.orderId);
  const debounceRef = useRef(null);
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  // Serial edit modal
  const [serialModal, setSerialModal]   = useState(null);
  const [serialInputs, setSerialInputs] = useState([]);
  const [serialSaving, setSerialSaving] = useState(false);

  // Inline DCR status editing
  const [dcrStatusSaving, setDcrStatusSaving] = useState(null); // orderItemId being saved

  // Inline invoice number editing
  const [invoiceEditing, setInvoiceEditing] = useState(null);   // orderId being edited
  const [invoiceValue, setInvoiceValue]     = useState('');
  const [invoiceSaving, setInvoiceSaving]   = useState(null);   // orderId being saved

  useEffect(() => { loadWarehouses(); }, []);

  // Debounce orderId — wait 500ms after user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedOrderId(filters.orderId);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [filters.orderId]);

  useEffect(() => {
    localStorage.setItem(FILTER_KEY, JSON.stringify(filters));
    loadData();
  }, [page, perPage, filters.dateFrom, filters.dateTo, filters.warehouseId, filters.dcrStatus, debouncedOrderId]);

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses();
      setWarehouseOptions((res?.data || []).map(w => ({ value: w.id, label: w.name })));
    } catch {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: perPage,
        ...(filters.dateFrom    && { dateFrom:     filters.dateFrom }),
        ...(filters.dateTo      && { dateTo:        filters.dateTo }),
        ...(filters.warehouseId && { warehouseId:   filters.warehouseId.value }),
        ...(filters.dcrStatus   && { dcrStatus:     filters.dcrStatus.value }),
        ...(debouncedOrderId    && { order_id:       debouncedOrderId }),
      };
      const res = await getSerialDCRRegister(params);
      if (res?.success) {
        let fetchedRows = res.data?.rows || [];

        // Operation-only users: hide rows where dcrDate is older than 7 days
        if (isOperationOnly) {
          const cutoff = new Date();
          cutoff.setHours(0, 0, 0, 0);
          cutoff.setDate(cutoff.getDate() - 7);
          fetchedRows = fetchedRows.filter(r => {
            if (!r.dcrDate) return true; // no dcrDate yet — keep visible
            const d = new Date(r.dcrDate);
            return d >= cutoff;
          });
        }

        setRows(fetchedRows);
        setTotalRows(res.data?.total || 0);
      }
    } catch {
      toast.error('Failed to load register');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters({ dateFrom: '', dateTo: '', warehouseId: null, dcrStatus: null, orderId: '' });
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      await exportSerialDCRRegister({
        ...(filters.dateFrom    && { dateFrom:   filters.dateFrom }),
        ...(filters.dateTo      && { dateTo:     filters.dateTo }),
        ...(filters.warehouseId && { warehouseId: filters.warehouseId.value }),
        ...(filters.dcrStatus   && { dcrStatus:  filters.dcrStatus.value }),
        format,
      });
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const openSerialModal = (row) => {
    setSerialModal(row);
    setSerialInputs(row.serialNumbers?.length > 0 ? [...row.serialNumbers] : ['']);
  };

  const closeSerialModal = () => { setSerialModal(null); setSerialInputs([]); };

  const handleDcrStatusChange = async (row, option) => {
    const newStatus = option?.value || null;
    setDcrStatusSaving(row.orderItemId);
    try {
      const res = await updateItemSerialNumbers(row.orderItemId, row.serialNumbers || [], newStatus);
      if (!res?.success) { toast.error(res?.message || 'Failed to update DCR status'); return; }
      setRows(prev => prev.map(r => r.orderItemId === row.orderItemId ? { ...r, dcrStatus: newStatus } : r));
      toast.success('DCR status updated');
    } catch {
      toast.error('Failed to update DCR status');
    } finally {
      setDcrStatusSaving(null);
    }
  };

  const handleInvoiceSave = async (row) => {
    const trimmed = invoiceValue.trim();
    setInvoiceSaving(row.orderId);
    try {
      const res = await updateOrderDetails(row.orderId, { invoice_number: trimmed });
      if (!res?.success) { toast.error(res?.message || 'Failed to update invoice number'); return; }
      setRows(prev => prev.map(r => r.orderId === row.orderId ? { ...r, invoiceNumber: trimmed } : r));
      toast.success('Invoice number updated');
      setInvoiceEditing(null);
    } catch {
      toast.error('Failed to update invoice number');
    } finally {
      setInvoiceSaving(null);
    }
  };

  const handleSerialSave = async () => {
    const serials = serialInputs.map(s => s.trim()).filter(Boolean);
    setSerialSaving(true);
    try {
      const res = await updateItemSerialNumbers(serialModal.orderItemId, serials);
      if (!res?.success) { toast.error(res?.message || 'Failed to save'); return; }
      setRows(prev => prev.map(r => r.orderItemId === serialModal.orderItemId ? { ...r, serialNumbers: serials } : r));
      toast.success('Serial numbers updated');
      closeSerialModal();
    } catch {
      toast.error('Failed to save serial numbers');
    } finally {
      setSerialSaving(false);
    }
  };

  const activeFilterCount = [filters.dateFrom, filters.dateTo, filters.warehouseId, filters.dcrStatus, filters.orderId].filter(Boolean).length;

  const columns = useMemo(() => [
    {
      name: 'Order Date',
      selector: row => row.orderDate,
      sortable: true,
      width: '110px',
      cell: row => {
        if (!row.orderDate) return <span style={{ color: '#6c757d' }}>—</span>;
        const d = new Date(row.orderDate);
        return <span style={{ fontSize: '12px' }}>{String(d.getDate()).padStart(2,'0')}/{String(d.getMonth()+1).padStart(2,'0')}/{d.getFullYear()}</span>;
      },
    },
    {
      name: 'Warehouse',
      selector: row => row.warehouseName,
      sortable: true,
      width: '130px',
      cell: row => (
        <span style={{ fontSize: '12px', color: '#212529' }}>{row.warehouseName || '—'}</span>
      ),
    },
    {
      name: 'Order ID',
      selector: row => row.orderId,
      sortable: true,
      width: '110px',
      cell: row => (
        <Link to={`/orders/${row.orderId}`} target={isMobile ? undefined : '_blank'} style={{ textDecoration: 'none' }}>
          <span style={{ fontWeight: '600', color: '#0d6efd', fontSize: '12px' }}>{row.orderId || '—'}</span>
        </Link>
      ),
    },
    {
      name: 'Invoice No.',
      selector: row => row.invoiceNumber,
      sortable: true,
      width: '160px',
      cell: row => {
        if (!canEdit) return (
          <span style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: '600', color: '#212529' }}>
            {row.invoiceNumber || <span style={{ color: '#6c757d', fontFamily: 'inherit', fontWeight: '400' }}>—</span>}
          </span>
        );
        if (invoiceEditing === row.orderId) return (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', width: '100%' }}>
            <input
              autoFocus
              type="text"
              value={invoiceValue}
              onChange={e => setInvoiceValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleInvoiceSave(row);
                if (e.key === 'Escape') setInvoiceEditing(null);
              }}
              className="form-control"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{ fontSize: '11px', padding: '2px 6px', fontFamily: 'monospace', width: '90px' }}
            />
            <button
              onClick={() => handleInvoiceSave(row)}
              disabled={invoiceSaving === row.orderId}
              className="btn btn-primary"
              style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px' }}
            >{invoiceSaving === row.orderId ? '...' : '✓'}</button>
            <button
              onClick={() => setInvoiceEditing(null)}
              className="btn btn-secondary"
              style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px' }}
            >✕</button>
          </div>
        );
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: '600', color: '#212529' }}>
              {row.invoiceNumber || <span style={{ color: '#6c757d', fontFamily: 'inherit', fontWeight: '400' }}>—</span>}
            </span>
            <button
              onClick={() => { setInvoiceEditing(row.orderId); setInvoiceValue(row.invoiceNumber || ''); }}
              className="btn btn-secondary"
              style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 8px' }}
            >Edit</button>
          </div>
        );
      },
    },
    {
      name: 'SKU ID',
      selector: row => row.skuCode,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ fontSize: '11px', fontFamily: 'monospace', background: '#f8f9fa', border: '1px solid #dee2e6', padding: '2px 6px', borderRadius: '3px', color: '#495057' }}>
          {row.skuCode || '—'}
        </span>
      ),
    },
    {
      name: 'SKU Name',
      selector: row => row.skuName,
      sortable: true,
      grow: 2,
      cell: row => <span style={{ fontSize: '12px', fontWeight: '500', color: '#212529' }}>{row.skuName}</span>,
    },
    {
      name: 'Serial Numbers',
      grow: 2,
      cell: row => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', padding: '4px 0' }}>
          {row.serialNumbers?.length > 0 ? (
            row.serialNumbers.map((sn, i) => (
              <span key={i} style={{ fontSize: '11px', fontFamily: 'monospace', background: '#cff4fc', border: '1px solid #b6effb', borderRadius: '3px', padding: '2px 6px', color: '#055160', fontWeight: '600' }}>
                {sn}
              </span>
            ))
          ) : (
            <span style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>No serials</span>
          )}
          {canEdit && (
            <button
              onClick={() => openSerialModal(row)}
              className="btn btn-secondary"
              style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 8px', marginLeft: '2px' }}
            >
              {row.serialNumbers?.length > 0 ? 'Edit' : '+ Add'}
            </button>
          )}
          {row.serialNumbers?.length > 0 && (
            <button
              onClick={() => {
                const text = row.serialNumbers.join('\n');
                navigator.clipboard.writeText(text).then(() => {
                  toast.success('Serial numbers copied!');
                }).catch(() => {
                  toast.error('Failed to copy');
                });
              }}
              className="btn btn-secondary"
              style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 8px', marginLeft: '2px' }}
              title="Copy all serial numbers"
            >Copy</button>
          )}
        </div>
      ),
    },
    {
      name: 'Customer Approved At',
      selector: row => row.serialApprovedAt,
      sortable: true,
      width: '175px',
      cell: row => (
        <span style={{ fontSize: '11px', color: '#495057' }}>
          {row.serialApprovedAt
            ? new Date(row.serialApprovedAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—'}
        </span>
      ),
    },
    {
      name: 'DCR Status',
      selector: row => row.dcrStatus,
      sortable: true,
      width: '210px',
      cell: row => canEdit ? (
        <div style={{ width: '100%', position: 'relative' }}>
          <Select
            options={DCR_STATUS_OPTIONS}
            value={DCR_STATUS_OPTIONS.find(o => o.value === row.dcrStatus) || null}
            onChange={option => handleDcrStatusChange(row, option)}
            isClearable
            isDisabled={dcrStatusSaving === row.orderItemId}
            placeholder="Set status..."
            styles={{
              ...selectStyles,
              control: (p, s) => ({ ...selectStyles.control(p, s), minHeight: '28px' }),
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {dcrStatusSaving === row.orderItemId && (
            <span style={{ fontSize: '10px', color: '#6c757d', marginTop: '2px', display: 'block' }}>Saving...</span>
          )}
        </div>
      ) : (
        (() => {
          const meta = DCR_STATUS_META[row.dcrStatus];
          if (!meta) return <span style={{ color: '#6c757d', fontSize: '11px' }}>—</span>;
          return <span className={meta.cls}>{meta.label}</span>;
        })()
      ),
    },
  ], [dcrStatusSaving, invoiceEditing, invoiceValue, invoiceSaving, isMobile]);

  return (
    <div className="container">

      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div className="breadcrumb" style={{ marginBottom: '4px' }}>
              <Link to="/orders">Orders</Link> / Serial Numbers &amp; DCR
            </div>
            <h1 className="page-title">Serial Numbers &amp; DCR Register</h1>
            <p className="page-subtitle">DCR-required SKU lines across all orders</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleExport('csv')}
              disabled={!!exporting}
              className="btn btn-secondary"
              style={{ fontSize: '11px', padding: '6px 12px' }}
            >
              {exporting === 'csv' ? 'Exporting...' : '↓ CSV'}
            </button>
            <button
              onClick={() => handleExport('xlsx')}
              disabled={!!exporting}
              className="btn btn-secondary"
              style={{ fontSize: '11px', padding: '6px 12px' }}
            >
              {exporting === 'xlsx' ? 'Exporting...' : '↓ Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`filters-card${isMobile && !filtersOpen ? ' mobile-collapsed' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 className="filters-title" style={{ marginBottom: 0 }}>Filter Register</h3>
          {activeFilterCount > 0 && (
            <button onClick={handleClearFilters} className="btn btn-secondary" style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 12px' : '4px 10px' }}>
              Clear filters ({activeFilterCount})
            </button>
          )}
          {isMobile && (
            <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
              {filtersOpen ? 'Hide filters' : 'Filters'}
            </button>
          )}
        </div>
        <div className="filters" style={{ gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)' }}>
          <div className="filter-group">
            <label className="form-label">Order ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search order ID..."
              value={filters.orderId}
              onChange={e => handleFilterChange('orderId', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.dateFrom}
              onChange={e => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.dateTo}
              onChange={e => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="form-label">Hub</label>
            <Select
              options={warehouseOptions}
              value={filters.warehouseId}
              onChange={v => handleFilterChange('warehouseId', v)}
              isClearable
              placeholder="All Hubs"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="filter-group">
            <label className="form-label">DCR Status</label>
            <Select
              options={DCR_STATUS_OPTIONS}
              value={filters.dcrStatus}
              onChange={v => handleFilterChange('dcrStatus', v)}
              isClearable
              placeholder="All Statuses"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e9ecef' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>
            Results
            {!loading && <span style={{ fontSize: '11px', fontWeight: '400', color: '#6c757d', marginLeft: '6px' }}>({totalRows} records)</span>}
          </span>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          progressPending={loading}
          progressComponent={
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>Loading...</div>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangePage={p => setPage(p)}
          onChangeRowsPerPage={(rpp) => { setPerPage(rpp); setPage(1); }}
          highlightOnHover
          striped
          noDataComponent={
            <div style={{ padding: '48px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>📋</div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', marginBottom: '4px' }}>No records found</div>
              <div style={{ fontSize: '12px' }}>Try adjusting your filters</div>
            </div>
          }
          customStyles={customTableStyles}
        />
      </div>

      {/* Serial Edit Modal */}
      {serialModal && (
        <div
          onClick={closeSerialModal}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '460px', height: 'min(520px, 90vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', border: '1px solid #e9ecef', overflow: 'hidden' }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#212529' }}>Edit Serial Numbers</div>
                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                  {serialModal.skuName}
                  {serialModal.skuCode && <span style={{ marginLeft: '6px', fontFamily: 'monospace', background: '#e9ecef', padding: '1px 5px', borderRadius: '3px' }}>{serialModal.skuCode}</span>}
                </div>
              </div>
              <button onClick={closeSerialModal} style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '20px', cursor: 'pointer', color: '#6c757d', lineHeight: 1, padding: '0 4px', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* Serial Inputs */}
              {serialInputs.map((sn, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', width: '22px', textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                  <input
                    type="text"
                    value={sn}
                    onChange={e => {
                      const updated = [...serialInputs];
                      updated[idx] = e.target.value;
                      setSerialInputs(updated);
                    }}
                    placeholder={`Serial number ${idx + 1}`}
                    className="form-control"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    style={{ fontFamily: 'monospace', fontSize: '12px', padding: '5px 8px' }}
                    autoFocus={idx === 0}
                  />
                  <button
                    onClick={() => {
                      const updated = [...serialInputs];
                      updated.splice(idx, 1);
                      setSerialInputs(updated.length > 0 ? updated : ['']);
                    }}
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: isMobile ? '22px' : '18px', lineHeight: 1, padding: '0 2px', flexShrink: 0, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}
                  >×</button>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e9ecef', background: '#f8f9fa' }}>
              <button
                onClick={() => setSerialInputs(prev => [...prev, ''])}
                className="btn btn-secondary"
                style={{ fontSize: '11px', padding: '5px 12px', marginRight: 'auto' }}
              >+ Add Serial</button>
              <button onClick={closeSerialModal} className="btn btn-secondary" style={{ fontSize: '11px', padding: '5px 12px' }}>Cancel</button>
              <button
                onClick={handleSerialSave}
                disabled={serialSaving}
                className="btn btn-primary"
                style={{ fontSize: '11px', padding: '5px 14px' }}
              >{serialSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SerialDCRRegister;
